/**
 * The clock on a swiping round.
 *
 * Swiping has no natural end other than an exhausted deck, and a deck holds
 * every title the filters allow — hundreds of them. Left alone, a group that
 * only wanted to pick a film keeps swiping until somebody gets bored and says
 * so. The timer is the group deciding *in advance* how long it wants to
 * spend: when it runs out, the shortlist vote settles the evening instead of a
 * person having to.
 *
 * It is never the last word, though. The time being up is put to the host
 * first, with the option to add more — a countdown that ends the round while
 * everybody is still enjoying it would be the app overruling the room.
 */
/** Minutes a freshly enabled timer is set to. */
export const DEFAULT_TIMER_MINUTES = 5;
/** Minutes the "add more time" button offers by default. */
export const DEFAULT_EXTENSION_MINUTES = 5;
export const MIN_TIMER_MINUTES = 1;
/** Four hours. Past this the timer is not what is keeping the evening going. */
export const MAX_TIMER_MINUTES = 240;
/** Whether a number is a timer length the server will accept. */
export function isValidTimerMinutes(minutes) {
    return Number.isInteger(minutes) && minutes >= MIN_TIMER_MINUTES && minutes <= MAX_TIMER_MINUTES;
}
/** The nearest acceptable timer length; what a stray input box value becomes. */
export function clampTimerMinutes(minutes) {
    if (!Number.isFinite(minutes))
        return DEFAULT_TIMER_MINUTES;
    return Math.min(MAX_TIMER_MINUTES, Math.max(MIN_TIMER_MINUTES, Math.round(minutes)));
}
/**
 * A timer length read out of a text box.
 *
 * `parseFloat` rather than `Number` on purpose: `Number('')` is zero, which
 * would turn an emptied field into a one-minute round. There is a sensible
 * answer to "how long", so a blank or unreadable box reads as that rather
 * than as an error somebody has to clear before they can carry on.
 */
export function parseTimerMinutes(value) {
    return clampTimerMinutes(Number.parseFloat(value));
}
/**
 * A duration as a running clock: `4:07`, `0:09`, `1:02:00`.
 *
 * Seconds are always two digits so the number stops jumping about as it
 * counts down, and anything negative reads as `0:00` rather than as a minus
 * sign appearing under a heading that says how much time is left.
 */
export function formatCountdown(ms) {
    const total = Math.max(0, Math.ceil(ms / 1000));
    const seconds = total % 60;
    const minutes = Math.floor(total / 60) % 60;
    const hours = Math.floor(total / 3600);
    const pad = (value) => String(value).padStart(2, '0');
    return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}
